import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { wrapText } from "../ChartUtil";
import GraphDownloadButton from "../../Button/GraphDownloadButton/GraphDownloadButton.js";
import { getContrastColor } from "../ChartUtil";
import "./MultiLineChart.js";
import ErrorBoundary from "../../ErrorBoundary/ErrorBoundary";

function yearDifference(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  let years = end?.getFullYear() - start?.getFullYear();

  // Adjust if end month/day is before start month/day
  if (
    end.getMonth() < start.getMonth() ||
    (end.getMonth() === start.getMonth() && end.getDate() < start.getDate())
  ) {
    years--;
  }

  return years;
}

const MultiLineChartInner = ({ data, height = 520, title, colorArray = [] }) => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [graphConfig, setGraphConfig] = useState(data.config);
  const [graphData, setGraphData] = useState(data.data);
  const [reportData, setReportData] = useState(data.data);
  const [visibleSeries, setVisibleSeries] = useState(
    data.data.map((d) => d.label),
  );
  const [btnTooltopMessage, setBtnTooltopMessage] = useState("");

  useEffect(() => {
    if (data) {
      setGraphData(data.data);
    }
  }, [data]);

  /* RESPONSIVE WIDTH */
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (!entries.length) return;
      setWidth(entries[0].contentRect.width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!width) return;

    d3.select(svgRef.current).selectAll("*").remove();

    const margin = { top: 30, right: 220, bottom: 40, left: 80 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const sliderWidth = innerWidth;
    const sliderHeight = 20;
    const circleRadius = 16;
    let leftX = 0;
    let rightX = innerWidth;
    const TOOLTIP_WIDTH = 40;
    const TOOLTIP_HEIGHT = 20;
    const TOOLTIP_GAP = 4;

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    /* PARSE DATES */
    // const parseDate = d3.timeFormat("%b %d, %Y");
    const parseDate = d3.timeParse("%Y-%m-%d");
    const formatDate = d3.timeFormat("%b %d, %Y");

    const series = graphData.map((s) => ({
      label: s.label,
      values: s.values.map((v) => ({
        date: parseDate(v.date),
        value: v.value,
      })),
    }));

    const filteredSeries = series.filter((s) =>
      visibleSeries.includes(s.label),
    );

    setReportData(filteredSeries);
    const allValues = series.flatMap((s) => s.values);

    const parsedData = data.data[0].values.map((d) => ({
      ...d,
      date: new Date(d.date),
    }));

    /* SCALES */
    const x = d3
      .scaleTime()
      .domain(d3.extent(allValues, (d) => d.date))
      .range([0, innerWidth]);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(allValues, (d) => d.value)])
      .nice()
      .range([innerHeight, 0]);

    const minDate = d3.min(parsedData, (d) => d.date);
    const maxDate = d3.max(parsedData, (d) => d.date);

    const dateScale = d3
      .scaleTime()
      .domain([minDate, maxDate]) // e.g. [2000, 2024]
      .range([0, sliderWidth]);

    /* COLORS (matched to image) */
    const color = d3
      .scaleOrdinal()
      .domain(series.map((d) => d.label))
      .range([
        ...colorArray,
        // "#6B6ECF", // Total
        // "#7FC0E5", // Verified OA
        // "#7ED6B2", // Reuse Terms
        // "#73B36B", // Archived
        // "#E26AC2", // Datasets
        // "#67E0B1", // ORCID
        // "#E0B15F", // Funder URLs
      ]);

    /* GRIDLINES */
    g.append("g")
      .call(d3.axisLeft(y).ticks(8).tickSize(-innerWidth).tickFormat(""))
      .selectAll("line")
      .attr("stroke", "#E5E7EB");

    g.selectAll(".domain").remove();

    /* AXES */
    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .style("text-anchor", "center")
      .call(d3.axisBottom(x).ticks(6))
      .style("font-size", "15px");

    g.append("g").call(d3.axisLeft(y).ticks(8)).style("font-size", "15px");

    /* LINE GENERATOR */
    const line = d3
      .line()
      .x((d) => x(d.date))
      .y((d) => y(d.value))
      .curve(d3.curveMonotoneX);

    if (filteredSeries.length >= 1) {
      /* LINES */
      g.selectAll(".line")
        .data(filteredSeries)
        // .data(series)
        .enter()
        .append("path")
        .attr("fill", "none")
        .attr("stroke", (d) => color(d.label))
        .attr("stroke-width", 2)
        .attr("d", (d) => line(d.values));
    }

    /* LEGEND */
    const legend = svg
      .append("g")
      .attr(
        "transform",
        `translate(${margin.left + innerWidth + 20},${margin.top})`,
      );

    const legendItem = legend
      .selectAll(".legend-item")
      .data(series)
      .enter()
      .append("g")
      .attr("transform", (d, i) => `translate(0, ${i * 40})`)
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        toggleSeries(d.label);
      });

    legendItem
      .append("rect")
      .attr("width", 16)
      .attr("height", 16)
      .attr("rx", 3)
      .attr("ry", 3)
      .attr("fill", "white")
      .attr("stroke", "#7878b9")
      .attr("stroke-width", 1);
    // .attr("fill", (d) => color(d.label));

    legendItem
      .append("path")
      .attr("d", "M3 8 L7 12 L13 4")
      .attr("fill", "none")
      .attr("stroke", "#7878b9")
      .attr("stroke-width", 2)
      .style("display", (d) =>
        visibleSeries.includes(d.label) ? "block" : "none",
      );

    const legentText = legendItem
      .append("text")
      .attr("x", 26)
      .attr("y", 12)
      .style("font-size", "16px")
      .style("fill", (d) => color(d.label))
      .text((d) => d.label);

    legendItem
      .selectAll("text")
      .attr("dy", "0")
      .call(wrapText, margin.right - 20, 2, true, 26);

    legendItem
      .selectAll("text")
      .append("title")
      .text((d) => d.label);

    /*TOOLTIP*/
    const overlay = svg
      .append("rect")
      .attr("class", "overlay")
      .attr("x", margin.left - 1) // -1 is to read first data easily
      .attr("y", margin.top)
      .attr("width", innerWidth)
      .attr("height", innerHeight)
      .style("fill", "none")
      .style("pointer-events", "all");

    const hoverLine = svg
      .append("line")
      .attr("class", "hover-line")

      .attr("x1", margin.left)
      .attr("x2", margin.left)
      .attr("y1", margin.top)
      .attr("y2", margin.top + innerHeight)
      .style("stroke", "#000")
      .style("stroke-width", 1)
      .style("opacity", 0);

    const bottomLabelGroup = svg
      .append("g")
      .attr("class", "bottom-label")
      .style("display", "none");

    bottomLabelGroup
      .append("rect")
      .attr("class", "bottom-label-rect")
      .attr("fill", "#333");

    // Text
    bottomLabelGroup
      .append("text")
      .attr("class", "bottom-label-text")
      .attr("x", 40) // width / 2
      .attr("y", 11) // height / 2
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .style("font-size", "12px")
      .style("fill", "#fff");

    const tooltipGroup = svg.append("g").style("display", "none");

    const tooltips = tooltipGroup
      .selectAll(".line-chart-tooltip")
      .data(series)
      .enter()
      .append("g")
      .attr("class", "line-chart-tooltip");

    tooltips
      .append("text")
      .attr("x", 8)
      .attr("dy", "0.35em")
      .style("font-size", "12px")
      .style("fill", (d) => "#000");

    tooltips
      .insert("rect", "text")
      .attr("x", 0)
      .attr("y", -10)
      .attr("fill", (d) => color(d.label));

    tooltipGroup
      .on("mouseover", () => {
        hoverLine.style("opacity", 1).raise(); //Mouse in always on the hoverLine and not on the Overlay.This will prevent hoverLine Opacity to 0
        tooltipGroup.style("display", null);
        bottomLabelGroup.style("display", null);
        // tooltipGroup.raise();
      })
      .on("mouseout", () => {
        hoverLine.style("opacity", 0);
        tooltipGroup.style("display", "none");
        bottomLabelGroup.style("display", "none");
      });

    hoverLine
      .on("mouseover", () => {
        if (filteredSeries.length < 1) return;
        hoverLine.style("opacity", 1).raise(); //Mouse in always on the hoverLine and not on the Overlay.This will prevent hoverLine Opacity to 0
        tooltipGroup.style("display", null);
        bottomLabelGroup.style("display", null);
        // tooltipGroup.raise();
      })
      .on("mouseout", () => {
        hoverLine.style("opacity", 0);
        tooltipGroup.style("display", "none");
        bottomLabelGroup.style("display", "none");
      });

    overlay
      .on("mouseout", () => {
        hoverLine.style("opacity", 0);
        tooltipGroup.style("display", "none");
        bottomLabelGroup.style("display", "none");
        // d3.selectAll(".tooltip-rect").remove();
      })
      .on("mousemove", function (event, data) {
        if (filteredSeries.length < 1) return;

        const tooltipPositions = [];

        const [mouseX, mouseY] = d3.pointer(event);

        const xValue = x.invert(mouseX - margin.left);

        // Find closest data index
        const bisect = d3.bisector((d) => d.date).left;

        const index = bisect(filteredSeries[0].values, xValue);

        const d = filteredSeries[0].values[index];
        if (!d) return;

        // Move vertical line
        hoverLine.attr("x1", mouseX).attr("x2", mouseX);

        // Update tooltips for ALL lines
        tooltips.each(function (key, idx) {
          const point = key.values[index];

          if (point == null) return;

          bottomLabelGroup.attr(
            "transform",
            `translate(${mouseX}, ${innerHeight + margin.bottom})`,
          );

          d3.select(".bottom-label-rect")
            .attr("width", 80)
            .attr("height", 22)
            .text(formatDate(point.date));
          d3.select(".bottom-label-text").text(formatDate(point.date));

          const yValue = y(point.value);
          let yPosition = yValue + margin.top;
          let xPosition = mouseX;

          let collision = true;
          let pushDirection = -1; // -1 means push UP, 1 means push DOWN

          const gap = typeof TOOLTIP_GAP !== "undefined" ? TOOLTIP_GAP : 5;
          const requiredClearance = TOOLTIP_HEIGHT + gap;

          while (collision) {
            collision = false;

            for (const p of tooltipPositions) {
              if (Math.abs(p - yPosition) < requiredClearance) {
                if (pushDirection === -1) {
                  // Strategy A: Try pushing UP
                  // Add an extra 1px to guarantee we clear the floating-point threshold
                  let proposedY = p - requiredClearance - 1;

                  if (proposedY < margin.top) {
                    pushDirection = 1; // Hit the ceiling, push DOWN instead
                    yPosition = p + requiredClearance + 1; // +1px buffer
                  } else {
                    yPosition = proposedY;
                  }
                } else {
                  // Strategy B: Push DOWN
                  yPosition = p + requiredClearance + 1; // +1px buffer
                }

                collision = true;
                break;
              }
            }
          }

          // Push as a raw number to prevent NaN errors
          if (visibleSeries.includes(key.label)) {
            tooltipPositions.push(yPosition);
          }

          //.......OLD CODE START..........
          // let collision = true;

          // while (collision) {
          //   collision = false;

          //   for (const p of tooltipPositions) {
          //     if (Math.abs(p - yPosition) < TOOLTIP_HEIGHT) {
          //       console.log("P =>", p);
          //       yPosition = p - TOOLTIP_HEIGHT - TOOLTIP_GAP;
          //       collision = true;
          //       break;
          //     }
          //     // else if (
          //     //   Math.abs(p - yPosition) < TOOLTIP_HEIGHT &&
          //     //   yPosition <= innerHeight / 2
          //     // ) {
          //     //   yPosition = p + TOOLTIP_HEIGHT; //+ TOOLTIP_GAP;
          //     //   collision = true;
          //     //   break;
          //     // }
          //   }
          // }

          // tooltipPositions.push(yPosition);

          // // Collision detection
          // const hasCollision = tooltipPositions.some(
          //   (p) => Math.abs(p.yPosition - yPosition) < TOOLTIP_HEIGHT, //+ TOOLTIP_GAP,
          // );

          //.......OLD CODE END..........

          // save position
          if (visibleSeries.includes(key.label)) {
            tooltipPositions.push({ yPosition });
          }

          const textNode = d3.select(this).select("text").node();
          const bbox = textNode.getBBox();

          if (bbox.width) {
            hoverLine.style("display", null).style("opacity", 1).raise();
            tooltipGroup.style("display", null);
            bottomLabelGroup.style("display", null);
          } else {
            hoverLine.style("display", "none").style("opacity", 0);
            tooltipGroup.style("display", "none");
            bottomLabelGroup.style("display", "none");
          }

          //.......OLD CODE START..........
          // if (hasCollision) {
          //   // move to LEFT of hover line
          //   xPosition = mouseX - bbox.width - 15;
          // }
          //.......OLD CODE END..........

          if (innerWidth - xPosition + margin.left < bbox.width) {
            xPosition = mouseX - bbox.width - 15;
          }

          d3.select(this)
            .attr("transform", `translate(${xPosition}, ${yPosition})`)
            .style("display", (d) =>
              visibleSeries.includes(d.label) && bbox.width ? "block" : "none",
            )
            .select("text")
            .style("fill", (d) => getContrastColor(color(d.label)))
            .text((d) => {
              return key.label + ": " + point.value.toLocaleString("en-US");
            });

          d3.select(this)
            .select("rect")
            .attr("x", 0)
            .attr("y", -10)
            .attr("width", bbox.width + 15)
            .attr("height", TOOLTIP_HEIGHT)
            .attr("fill", (d) => color(d.label));
        });
      });
  }, [data, width, height, visibleSeries, graphData]);

  /* TOGGLE LINE */
  const toggleSeries = (label) => {
    // if()
    setVisibleSeries((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  };

  useEffect(() => {
    // console.log("report data =>", reportData);
    if (reportData.length >= 1) {
      const yearsDiff = yearDifference(
        reportData[0].values[0].date,
        reportData?.[0]?.values?.at(-1).date,
      );
      yearsDiff > 3
        ? setBtnTooltopMessage(
            "Time series data downloads are restricted to a maximum 3-year range. Please modify your date selection accordingly.",
          )
        : setBtnTooltopMessage("");
    }
  }, [reportData]);

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", minWidth: "768px", position: "relative" }}
    >
      <GraphDownloadButton
        svg={svgRef.current}
        fileName={title}
        // jsonData={reportData}
        btnTooltopMessage={btnTooltopMessage}
        jsonData={
          data?.data.filter((item) => visibleSeries.includes(item.label)) || {}
        }
      />

      <svg ref={svgRef} style={{ overflow: "visible" }} />
      <div className=""></div>
    </div>
  );
};

const MultiLineChart = (props) => (
  <ErrorBoundary>
    <MultiLineChartInner {...props} />
  </ErrorBoundary>
);

export default MultiLineChart;
